import styled from '@emotion/styled';
import { Lock } from 'react-feather';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Constants } from '@trinity/chat-components/components/Theme';

const TEMPLATES = [
  { id: 'message-selection', name: 'Message Selection', description: 'Test and rank marketing messages with your synthetic audience to identify the most compelling and relevant content for each segment.', locked: false },
  { id: 'atu-brand-tracking', name: 'ATU Brand Tracking', description: 'Track awareness, trial, and usage metrics over time to measure brand funnel progression and identify conversion bottlenecks across HCP segments.', locked: true },
  { id: 'promo-effectiveness', name: 'Promotional Effectiveness / Detail Recall', description: 'Evaluate detail aid recall, message retention, and promotional impact to optimize rep-delivered content and sales force effectiveness.', locked: true },
  { id: 'launch-tracking', name: 'Pre-launch and Launch Tracking', description: 'Monitor pre-launch awareness, intent-to-prescribe, and early adoption signals to calibrate launch strategy and resource allocation.', locked: true },
  { id: 'message-creative-testing', name: 'Message and Creative Testing', description: 'Test message concepts, visual creative, and value propositions with synthetic audiences to optimize campaign assets before deployment.', locked: true },
  { id: 'market-landscape', name: 'Market Landscape & Competitive Intelligence Pulse', description: 'Capture competitive positioning, prescribing rationale, and market dynamics to inform strategic planning and differentiation.', locked: true },
  { id: 'patient-journey-pulse', name: 'Patient Journey Treatment-Pathway Pulse', description: 'Map treatment decision points, switching triggers, and therapy sequencing patterns along the patient pathway from diagnosis through ongoing care.', locked: true },
  { id: 'hcp-satisfaction', name: 'HCP Satisfaction and Engagement Pulse', description: 'Assess HCP satisfaction with engagement touchpoints, content relevance, and communication channels to optimize the omnichannel experience.', locked: true },
  { id: 'patient-support', name: 'Patient Support Program Feedback Pulse', description: 'Gather feedback on patient support program awareness, enrollment friction, and perceived value to improve program design and uptake.', locked: true },
  { id: 'digital-media', name: 'Digital Media and Channel Tracking Pulse', description: 'Track digital channel engagement, content consumption patterns, and platform preferences to optimize digital media mix and targeting.', locked: true },
  { id: 'rapid-issue', name: 'Rapid Issue Safety or Access Pulse', description: 'Quickly assess HCP perceptions around emerging safety signals, access barriers, or formulary changes to enable rapid response.', locked: true },
];

type Props = {
  onTemplateSelect: (templateId: string) => void;
  onCancel: () => void;
};

export const SurveyTemplateSelector = ({ onTemplateSelect, onCancel }: Props) => {
  return (
    <Root>
      <Content>
        <Header>
          <StyledAutoAwesomeIcon />
          <Title>Survey Templates</Title>
        </Header>
        <ScrollArea>
          {TEMPLATES.map((template) => (
            <TemplateCard
              key={template.id}
              $locked={template.locked}
              onClick={() => {
                if (!template.locked) onTemplateSelect(template.id);
              }}
            >
              <TemplateCardContent>
                <TemplateNameRow>
                  <TemplateName $locked={template.locked}>{template.name}</TemplateName>
                  {template.locked && <Lock size={14} />}
                </TemplateNameRow>
                <TemplateDescription $locked={template.locked}>{template.description}</TemplateDescription>
              </TemplateCardContent>
            </TemplateCard>
          ))}
        </ScrollArea>
        <Footer>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
        </Footer>
      </Content>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
  border-radius: ${Constants.Modal.BorderRadius}px;
  height: 100%;
  width: 100%;
  position: relative;
`;

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--pri-01);
`;

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px 0;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 5px 8px 8px;
`;

const TemplateCard = styled.div<{ $locked?: boolean }>`
  display: flex;
  background: ${(p) => (p.$locked ? '#fafafa' : '#ffffff')};
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 8px;
  cursor: ${(p) => (p.$locked ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$locked ? 0.6 : 1)};
  transition: all 0.2s;
  color: ${(p) => (p.$locked ? '#999' : '#666')};

  &:hover {
    ${(p) =>
      !p.$locked &&
      `
      border-color: #6366f1;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    `}
  }

  &:active {
    ${(p) => !p.$locked && 'transform: translateY(0);'}
  }
`;

const TemplateCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const TemplateNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TemplateName = styled.div<{ $locked?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.$locked ? '#999' : '#1a1a1a')};
`;

const TemplateDescription = styled.div<{ $locked?: boolean }>`
  font-size: 12px;
  line-height: 1.5;
  color: ${(p) => (p.$locked ? '#bbb' : '#666')};
`;

const Footer = styled.div`
  padding: 8px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #d3d3d3;
  border-radius: 6px;
  background: transparent;
  color: #626262;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #999;
    background: #fafafa;
  }
`;
